import { useCallback, useEffect, useRef, useState } from 'react';

const WS_URL = import.meta.env.VITE_WS_URL ?? 'ws://localhost:8080';
const RECONNECT_DELAY_MS = 3000;

export interface HubPlayer {
  id: string;
  name: string;
  class: string;
  x: number;
  y: number;
}

interface UseHubWebSocketOptions {
  /** Bearer token — the hook no-ops while this is falsy (no session yet). */
  token: string | null | undefined;
}

export interface UseHubWebSocketReturn {
  /** Every OTHER connected, not-in-battle player currently in the Hub. */
  otherPlayers: HubPlayer[];
  /** Tell everyone else the local player just moved (call after the HTTP
   *  POST /hub/move succeeds — this never persists anything by itself). */
  sendMove: (x: number, y: number) => void;
  connected: boolean;
}

/**
 * Real-time presence for the Hub (the city map) — see
 * backend/src/WebSocket/HubWebSocketServer.php for the protocol. Mirrors
 * useWebSocket.ts's connect/reconnect shape, but for the Hub's own
 * JOIN_HUB/MOVE/HUB_STATE/PLAYER_* messages instead of combat actions.
 * Disconnecting (tab closed, or this component unmounting because the
 * player started a battle) is what makes a player disappear from everyone
 * else's Hub — there is no separate "in combat" flag to keep in sync.
 */
export function useHubWebSocket({ token }: UseHubWebSocketOptions): UseHubWebSocketReturn {
  const wsRef = useRef<WebSocket | null>(null);
  const reconnectTimer = useRef<ReturnType<typeof setTimeout> | null>(null);
  const shouldReconnect = useRef(true);
  const playersById = useRef<Map<string, HubPlayer>>(new Map());

  const [otherPlayers, setOtherPlayers] = useState<HubPlayer[]>([]);
  const [connected, setConnected] = useState(false);

  const publish = useCallback(() => {
    setOtherPlayers(Array.from(playersById.current.values()));
  }, []);

  useEffect(() => {
    if (!token) return;
    shouldReconnect.current = true;

    const connect = () => {
      const state = wsRef.current?.readyState;
      if (state === WebSocket.OPEN || state === WebSocket.CONNECTING) return;

      const ws = new WebSocket(WS_URL);
      wsRef.current = ws;

      ws.onopen = () => {
        setConnected(true);
        ws.send(JSON.stringify({ type: 'JOIN_HUB', token }));
      };

      ws.onmessage = (event: MessageEvent<string>) => {
        let msg: any;
        try {
          msg = JSON.parse(event.data);
        } catch {
          return;
        }
        switch (msg.type) {
          case 'HUB_STATE':
            playersById.current = new Map((msg.players ?? []).map((p: HubPlayer) => [p.id, p]));
            publish();
            break;
          case 'PLAYER_JOINED':
            if (msg.player) {
              playersById.current.set(msg.player.id, msg.player);
              publish();
            }
            break;
          case 'PLAYER_MOVED': {
            const existing = playersById.current.get(msg.id);
            if (existing) {
              playersById.current.set(msg.id, { ...existing, x: msg.x, y: msg.y });
              publish();
            }
            break;
          }
          case 'PLAYER_LEFT':
            if (playersById.current.delete(msg.id)) publish();
            break;
        }
      };

      ws.onclose = () => {
        setConnected(false);
        wsRef.current = null;
        if (shouldReconnect.current) {
          reconnectTimer.current = setTimeout(connect, RECONNECT_DELAY_MS);
        }
      };

      ws.onerror = () => ws.close();
    };

    connect();

    return () => {
      shouldReconnect.current = false;
      if (reconnectTimer.current) clearTimeout(reconnectTimer.current);
      wsRef.current?.close();
      wsRef.current = null;
      playersById.current = new Map();
      setOtherPlayers([]);
      setConnected(false);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [token]);

  const sendMove = useCallback((x: number, y: number) => {
    if (wsRef.current?.readyState === WebSocket.OPEN) {
      wsRef.current.send(JSON.stringify({ type: 'MOVE', x, y }));
    }
  }, []);

  return { otherPlayers, sendMove, connected };
}
